import { Button as DsmButton, Checkbox } from '@kcftech/design-system';
import { Button } from 'components/button/Button';
import { CollapsiblePane } from 'components/expandable_pane/CollapsiblePane';
import { Textarea } from 'components/forms/Textarea';
import { Spinner } from 'components/spinner/Spinner';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import { FaultTypesInput } from 'pages/Desk/issues/fault_type/FaultTypesInput';
import { IssuePriorityDropdown } from 'pages/Desk/issues/IssuePriorityDropdown';
import { IssueSeverityDropdown } from 'pages/Desk/issues/IssueSeverityDropdown';
import { UsersInput } from 'pages/Desk/issues/UsersInput';
import styled, { css } from 'styled-components';

export const IssueModalTitleContainer = styled.div`
    flex-grow: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
`;

export const IssueModalTitleButtonsContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 1rem;
`;

export const IssueModalContentContainer = styled.div`
    background-color: var(--background-paper-elevation-2);
    display: grid;
    grid-template-areas:
        'primary-bar panel'
        'trend panel';
    grid-template-columns: 1fr auto;
    grid-template-rows: auto 1fr;
    height: 100%;
`;

export const HealthRangeIssueModalContentContainer = styled(IssueModalContentContainer)`
    grid-template-areas:
        'health-ranges primary-bar panel'
        'health-ranges trend panel';

    grid-template-columns: auto 1fr auto;
    grid-template-rows: auto 1fr;
`;

export const IssueModalHealthRangesContainer = styled.div`
    grid-area: health-ranges;
    width: 30rem;
    padding: 2.5rem 1.5rem;
    background-color: var(--background-alpha-default);
    border-right: 1px solid var(--divider-default);
    display: flex;
    flex-direction: column;
    gap: 2rem;
`;

export const IssueModalContent = styled.div`
    height: 100%;
    width: 100%;
    grid-area: trend;
    overflow-y: auto;
`;

export const IssueModalPrimaryBar = styled.div`
    grid-area: primary-bar;
    height: 3.6rem;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.75rem;
    border-bottom: 1px solid var(--divider-default);
`;

export const SidePanelCollapseButton = styled(Button)<{ $isSidePanelOpen: boolean }>`
    justify-content: flex-end;
    height: 100%;
    border-radius: 0;
    background-color: var(--surface-dark);
    color: white;

    @media ${MediaQueries.mediumAndDown} {
        position: fixed;
        right: ${({ $isSidePanelOpen }) => ($isSidePanelOpen ? '301px' : '0px')};
        z-index: 2;
        top: 78px;
        height: 36px;
    }
`;

export const IssueModalSidePanelContainer = styled.div<{ $isOpen: boolean }>`
    grid-area: panel;
    height: 100%;
    width: 32rem;
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
    background-color: var(--background-alpha-default);
    border-left: 1px solid var(--divider-default);
    z-index: 2;

    @media ${MediaQueries.mediumAndDown} {
        position: fixed;
        right: 0;
        bottom: 0;
        top: 78px;
    }
`;

export const IssueModalSidePanelHeader = styled.div`
    background-color: var(--surface-dark);
    display: grid;
    grid-template-columns: 1fr 1fr;
    height: 100%;
    padding-right: 0.5rem;
`;

export const IssueModalSidePanelTab = styled.button<{ $isSelected: boolean }>`
    cursor: pointer;
    font-size: 1.4rem;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 1rem;
    color: ${({ $isSelected }) => ($isSelected ? 'var(--text-primary)' : 'white')};
    border: none;
    background-color: ${({ $isSelected }) =>
        $isSelected
            ? 'var(--background-default)'
            : 'var(--background-multicolors-default)'};
`;

export const IssueModalSectionContainer = styled.div<{ $isOpen: boolean }>`
    height: 100%;
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
`;

export const Separator = styled.div`
    border-top: 1px solid var(--divider-default);
`;

export const DataSectionLabel = styled.div`
    font-size: 1.8rem;
    font-weight: 500;
`;

export const DataSubSectionLabel = styled.div`
    font-size: 1.4rem;
    font-weight: 500;
    margin-bottom: 1rem !important;
`;

export const AddPlotButtonPreview = styled.span`
    font-size: 1.2rem;
    background: var(--primary-default);
    color: var(--foreground-inverse);
    text-align: center;
    border-radius: 3px;
    padding: 0.25rem 0.5rem;
    margin: 0 0.5rem;
    white-space: nowrap;
`;

export const ChartDataPlots = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

export const AlertsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 1rem 0;
`;

export const StyledCheckbox = styled(Checkbox)`
    ${({ checked }) =>
        !checked
            ? css`
                  background-color: var(--surface-raised) !important;
              `
            : ''}
`;

export const StyledTextarea = styled(Textarea)`
    border: 1px solid var(--input-border);
    background-color: var(--input-background);
    border-radius: 3px;
    margin-top: 0.5rem;
    font-size: 1.4rem;
`;

export const StyledSpinner = styled(Spinner)`
    color: white;
`;

export const HealthRangeCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    overflow-y: auto;
`;

export const HealthRangeCardContainer = styled.div`
    background: var(--surface-raised);
    box-shadow: var(--elevation-100);
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.6rem;
    margin: 2px;
    border-radius: 8px;
`;

export const HealthRangeFilterOption = styled(DsmButton)`
    text-align: left !important;
`;

export const ToggleSelectionCheckbox = styled(Checkbox)`
    ${({ checked, indeterminate }) =>
        !checked && !indeterminate
            ? css`
                  background-color: var(--surface-raised) !important;
                  border: 2px solid var(--divider-default) !important;
              `
            : ''}
`;

export const HealthRangeIndicatorsCollapsible = styled(CollapsiblePane)`
    border: none !important;
`;

export const HealthRangeIndicatorsCollapsibleHeader = styled.div<{ $expanded: boolean }>`
    display: flex;
    gap: 0.5rem;
    color: var(--text-link);
    padding-bottom: ${({ $expanded }) => ($expanded ? '1rem' : 0)};
`;

export const Count = styled.div`
    background-color: var(--text-link);
    color: var(--foreground-inverse);
    padding: 0 0.6rem;
    border-radius: 8px;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
`;

export type DisplayMode = 'modal' | 'panel';

export const KcfOnlyContainer = styled.div<{ $displayMode: DisplayMode }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--text-secondary);
    background-color: ${({ $displayMode }) =>
        $displayMode === 'modal' ? 'var(--input-background)' : 'var(--surface-raised)'};
    font-size: 1.4rem;
    padding: 0.6rem 1.2rem;
    border-radius: 3px;
`;

export const StyledUsersInput = styled(UsersInput)`
    margin-top: 0.5rem;
    border: 1px solid var(--input-border) !important;
    background-color: var(--input-background) !important;
`;

export const StyledFaultTypesInput = styled(FaultTypesInput)`
    border: 1px solid var(--input-border) !important;
    background-color: var(--input-background) !important;
    font-size: 1.4rem;
`;

export const StyledIssueSeverityDropdown = styled(IssueSeverityDropdown)`
    border: 1px solid var(--input-border) !important;
    background-color: var(--input-background) !important;
`;

export const StyledIssuePriorityDropdown = styled(IssuePriorityDropdown)`
    border: 1px solid var(--input-border) !important;
    background-color: var(--input-background) !important;
`;
